import React, { useState } from 'react';
import { transactionAPI } from '../services/api';
import { useScrollLock } from '../hooks/useScrollLock';

interface PDFDebugModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PDFDebugModal: React.FC<PDFDebugModalProps> = ({ isOpen, onClose }) => {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [debugResult, setDebugResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useScrollLock(isOpen);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setDebugResult(null);
      setError(null);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleDebug = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Debug button clicked, pdfFile:', pdfFile);
    
    if (!pdfFile) {
      console.log('No PDF file selected');
      setError('Please select a PDF file first');
      return;
    }

    console.log('Starting PDF debug for file:', pdfFile.name);
    setLoading(true);
    setError(null);

    try {
      const result = await transactionAPI.debugPDFParsing({
        file: pdfFile
      });
      
      console.log('Debug result:', result);
      setDebugResult(result);
    } catch (err: any) {
      console.error('Debug error:', err);
      setError(err.message || 'Failed to debug PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPdfFile(null);
    setDebugResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content pdf-debug-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔍 Debug PDF Parsing</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="debug-section">
            <h3>Upload ScotiaBank PDF</h3>
            <p>This will show you exactly what text is being extracted from your PDF and why transactions aren't being found.</p>
            
            <div style={{ marginBottom: '20px' }}>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={{ 
                  display: 'block',
                  marginBottom: '15px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
            </div>
            
            {pdfFile && (
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px',
                border: '2px solid #ddd'
              }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold' }}>
                  Selected: {pdfFile.name}
                </p>
                <div style={{ borderTop: '1px solid #ccc', paddingTop: '15px' }}>
                  <button 
                    type="button"
                    onMouseDown={(e) => {
                      console.log('Mouse down on button');
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      console.log('Button clicked! Event details:', {
                        type: e.type,
                        target: e.target,
                        currentTarget: e.currentTarget,
                        defaultPrevented: e.defaultPrevented
                      });
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      
                      // Add a small delay to see if this helps
                      setTimeout(() => {
                        handleDebug(e);
                      }, 10);
                    }}
                    disabled={loading}
                    style={{ 
                      padding: '10px 20px',
                      backgroundColor: loading ? '#ccc' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      pointerEvents: 'auto'
                    }}
                  >
                    {loading ? 'Analyzing...' : 'Debug PDF'}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="error-message">
                <p>❌ {error}</p>
              </div>
            )}

            {debugResult && (
              <div className="debug-results">
                <h3>Debug Results</h3>
                
                <div className="debug-section">
                  <h4>📊 Extraction Summary</h4>
                  <ul>
                    <li><strong>Text Length:</strong> {debugResult.extraction?.text?.length || debugResult.text?.length || 0} characters</li>
                    <li><strong>Is Scanned:</strong> {debugResult.extraction?.isScanned || debugResult.isScanned ? 'Yes' : 'No'}</li>
                    <li><strong>Confidence:</strong> {Math.round(((debugResult.extraction?.confidence || debugResult.confidence) || 0) * 100)}%</li>
                    <li><strong>Transactions Found:</strong> {debugResult.extraction?.transactionCount || debugResult.transactions?.length || 0}</li>
                  </ul>
                </div>

                {(debugResult.extraction?.text || debugResult.text) && (
                  <div className="debug-section">
                    <h4>📄 Extracted Text (First 2000 characters)</h4>
                    <div className="text-preview">
                      <pre>{(debugResult.extraction?.text || debugResult.text).substring(0, 2000)}</pre>
                    </div>
                  </div>
                )}

                {debugResult.transactions && debugResult.transactions.length > 0 && (
                  <div className="debug-section">
                    <h4>💰 Parsed Transactions</h4>
                    <div className="transactions-preview">
                      {debugResult.transactions.map((transaction: any, index: number) => (
                        <div key={index} className="transaction-item">
                          <div><strong>Date:</strong> {transaction.date}</div>
                          <div><strong>Description:</strong> {transaction.description}</div>
                          <div><strong>Amount:</strong> ${transaction.amount}</div>
                          <div><strong>Confidence:</strong> {Math.round(transaction.confidence * 100)}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {debugResult.transactions && debugResult.transactions.length === 0 && (
                  <div className="debug-section">
                    <h4>❌ No Transactions Found</h4>
                    <p>This means the PDF format doesn't match the expected patterns. We need to add ScotiaBank-specific parsing patterns.</p>
                  </div>
                )}

                {debugResult.error && (
                  <div className="debug-section">
                    <h4>⚠️ Extraction Error</h4>
                    <p>{debugResult.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFDebugModal;
